import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import personIcon from "../assests/personIcon.svg";
import { useContext, useEffect } from "react";
import { useMediaQuery } from "usehooks-ts";
import { Link } from "react-router-dom";
import { AuthContext } from "../providers/auth-provider";
import BrandMark from "./brand-mark";
import SideBarData from "../utils/data/sideBar";
import clsx from "clsx";
import {
  faAngleLeft,
  faRightFromBracket,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useSidebar } from "../store/use-sidebar";
import ThemeToggle from "./theme-toggle";

export default function LeftSidebar() {
  const { user, HandleUserLogOut } = useContext(AuthContext);
  const { collapsed: stored, toggleCollapsed, mobileOpen, closeMobile } = useSidebar();
  // Mobile drawer is always full width; the desktop mini mode never applies there.
  const isMobile = useMediaQuery("(max-width: 1024px)");
  const collapsed = stored && !isMobile;

  useEffect(() => {
    if (!isMobile) closeMobile();
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && closeMobile();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isMobile, mobileOpen, closeMobile]);

  const isActive = (path: string) => {
    return window.location.pathname === path;
  };

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm smallLaptop:block hidden"
          onClick={closeMobile}
        />
      )}

      <aside
        className={clsx(
          "group/sidebar relative h-full bg-surface border-r border-edge animate-fade-in-slow smallLaptop:animate-none flex flex-col transition-[width,transform] duration-300 ease-out",
          collapsed ? "w-[84px]" : "w-[258px]",
          "smallLaptop:fixed smallLaptop:top-0 smallLaptop:left-0 smallLaptop:h-screen smallLaptop:z-40 smallLaptop:w-[260px]",
          mobileOpen
            ? "smallLaptop:translate-x-0"
            : "smallLaptop:-translate-x-full"
        )}
      >
        {/* ----------------------- HEADER / LOGO ----------------------- */}
        <div
          className={clsx(
            "flex items-center px-4 pt-5 pb-4 border-b border-edge-faint transition-all duration-300",
            collapsed ? "justify-center smallLaptop:justify-between" : "justify-between"
          )}
        >
          <div
            className={clsx(
              "flex items-center overflow-hidden transition-all duration-500 ease-out",
              collapsed
                ? "w-0 opacity-0 -translate-x-3 smallLaptop:w-full smallLaptop:opacity-100 smallLaptop:translate-x-0"
                : "w-full opacity-100 translate-x-0"
            )}
          >
            <BrandMark size="sm" />
          </div>

          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label="Toggle sidebar"
            className="smallLaptop:hidden flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-edge bg-overlay text-light-gray hover:text-text-primary hover:border-indigo-400/40 hover:shadow-glow-sm transition-all duration-200"
          >
            <FontAwesomeIcon
              icon={faAngleLeft}
              className={clsx(
                "text-xs transition-transform duration-300",
                collapsed && "rotate-180"
              )}
            />
          </button>

          <button
            type="button"
            onClick={closeMobile}
            aria-label="Close menu"
            className="hidden smallLaptop:flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-edge bg-overlay text-light-gray hover:text-text-primary"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* ------------------------ NAV LIST --------------------------- */}
        <ul className="font-medium flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar py-2">
          {SideBarData.map((item) => {
            const active = isActive(item.route);
            return (
              <div
                className={item.label === "My Customers" ? "mt-6" : ""}
                key={item.route}
              >
                <li className="group relative">
                  <Link
                    to={item.route}
                    onClick={closeMobile}
                    className={clsx(
                      // one color for the whole row → icon + text inherit the exact same color
                      "relative flex items-center transition-colors duration-200 ease-out",
                      collapsed
                        ? "justify-center smallLaptop:justify-start gap-0 smallLaptop:gap-3.5 px-3 smallLaptop:px-6 py-3"
                        : "gap-3.5 px-6 py-3",
                      active
                        ? "bg-active-background text-blue"
                        : "text-light-gray hover:bg-active-background/50 hover:text-text-primary"
                    )}
                  >
                    {/* active right-edge indicator */}
                    {active && (
                      <span className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-l-full bg-blue" />
                    )}

                    <FontAwesomeIcon
                      icon={item.icon}
                      className="w-6 shrink-0 text-center text-[17px]"
                    />

                    <span
                      className={clsx(
                        "flex-1 text-[15px] whitespace-nowrap overflow-hidden",
                        active ? "font-semibold" : "font-medium",
                        collapsed ? "hidden smallLaptop:inline" : "inline"
                      )}
                    >
                      {item.label}
                    </span>

                    {collapsed && (
                      <span className="pointer-events-none smallLaptop:hidden absolute left-full ml-3 z-50 rounded-lg bg-surface-elevated border border-edge px-3 py-1.5 text-xs text-text-primary shadow-card opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                        {item.label}
                      </span>
                    )}
                  </Link>
                </li>

                {(!collapsed || window.innerWidth <= 1024) &&
                  item?.subData &&
                  item.subData.length > 0 && (
                    <ul className="ml-[3.35rem] flex flex-col">
                      {item.subData.map((subItem) => (
                        <Link
                          key={subItem.route}
                          to={subItem.route}
                          onClick={closeMobile}
                          className={clsx(
                            "my-1.5 cursor-pointer font-medium text-nowrap text-[15px] text-light-gray hover:text-text-primary transition-colors duration-200",
                            isActive(subItem.route) && "!text-blue"
                          )}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </ul>
                  )}
              </div>
            );
          })}
        </ul>

        <ThemeToggle collapsed={collapsed} />

        {/* ------------------------- FOOTER ---------------------------- */}
        <div
          className="relative border-t border-edge bg-surface-elevated cursor-pointer transition-colors duration-300 hover:bg-active-background"
          onClick={HandleUserLogOut}
          title="Log out"
        >
          <div
            className={clsx(
              "flex items-center gap-3 px-4 py-3",
              collapsed && "justify-center"
            )}
          >
            <img
              className="w-10 h-10 rounded-full ring-2 ring-indigo-500/30 shrink-0"
              src={personIcon}
              alt=""
            />
            {!collapsed && (
              <div className="flex flex-col min-w-0 flex-1">
                <p className="font-medium text-[14px] text-heading-color truncate">
                  {user?.name}
                </p>
                <p className="font-medium text-xs text-light-gray capitalize truncate">
                  {user?.type?.replace("-", " ")}
                </p>
              </div>
            )}
            {!collapsed && (
              <FontAwesomeIcon
                icon={faRightFromBracket}
                className="text-light-gray text-sm opacity-70"
              />
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
